import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './auth';
import { summarize } from './dday';

/**
 * 첫 로그인 소개 투어(선물 개봉). 풀스크린으로 헤더·하단탭까지 덮는다.
 * 끝까지 보거나 건너뛰면 호출자(onDone)가 localStorage에 기록 → 이후 평소 앱.
 *
 * 슬라이드는 아래 `slides` 배열로 구성된다. 새 장 = 객체 한 개 추가.
 * 정적 안내 장은 `body`(ReactNode)만 채우고, 퀴즈처럼 상호작용이 필요한 장은
 * `render`(다음 단계로 넘기는 next 콜백을 받는 커스텀 렌더)를 쓰면 된다.
 *
 * ⏳ 다음 대화에서 추가 예정(슬롯 위치 표시해 둠):
 *   - 퀴즈 장(데이터 주입)  - 대화분석 하이라이트 장
 */

export interface TourSlide {
  key: string;
  /** 큰 이모지(상단). render를 쓰면 생략 가능. */
  emoji?: string;
  /** 제목(없으면 미표시). */
  title?: string;
  /** 정적 본문. */
  body?: React.ReactNode;
  /** 상호작용 장(퀴즈 등)용 커스텀 렌더. 주어지면 emoji/title/body 대신 사용. */
  render?: (ctx: { next: () => void }) => React.ReactNode;
  /** 이 장에서 직접 둘러볼 메뉴 경로(예: '/letters'). 있으면 "둘러보기" 버튼 표시. */
  exploreRoute?: string;
  /** 둘러보기 버튼 라벨(예: '편지함'). */
  exploreLabel?: string;
}

function formatStartDate(d: string): string {
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return d;
  return `${y}년 ${Number(m)}월 ${Number(day)}일`;
}

export function OnboardingTour({
  onDone,
  welcomeOverride,
}: {
  onDone: () => void;
  /** 어드민 미리보기에서 저장 전 환영 문구를 즉시 반영하기 위한 override. */
  welcomeOverride?: string;
}) {
  const { boot } = useAuth();
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [exploring, setExploring] = useState(false);

  const slides = useMemo<TourSlide[]>(() => {
    if (!boot) return [];
    const list: TourSlide[] = [];

    // ① 인사 — 환영 메시지(어드민 작성). 비어 있으면 이 장은 생략.
    const welcome = (welcomeOverride ?? boot.settings.welcomeMessage ?? '').trim();
    if (welcome) {
      list.push({
        key: 'intro',
        emoji: '🎁',
        body: <p className="whitespace-pre-line text-lg leading-relaxed">{welcome}</p>,
      });
    }

    // ② 우리 — 사귄 날 + 함께한 일수.
    const { days } = summarize(boot.coupleMeta);
    list.push({
      key: 'us',
      emoji: '💞',
      body: (
        <div className="space-y-1">
          <p className="font-round text-4xl font-extrabold">D+{days}</p>
          <p className="text-base text-ink-soft">{formatStartDate(boot.coupleMeta.startDate)}</p>
        </div>
      ),
    });

    // ③ 편지함
    list.push({
      key: 'letters',
      emoji: '💌',
      title: '편지함',
      exploreRoute: '/letters',
      exploreLabel: '편지함',
      body: (
        <p className="text-base leading-relaxed">
          서로 편지를 주고받는 곳. 타임캡슐 편지도 보낼 수 있어.
        </p>
      ),
    });

    // ④ 룰렛 — 함께 결정하기
    list.push({
      key: 'roulette',
      emoji: '🎲',
      title: '룰렛',
      exploreRoute: '/roulette',
      exploreLabel: '룰렛',
      body: (
        <p className="text-base leading-relaxed">오늘 뭐 먹을지 같이 정하는 곳!</p>
      ),
    });

    // ⑤ 유령체스
    list.push({
      key: 'chess',
      emoji: '👻',
      title: '유령체스',
      exploreRoute: '/chess',
      exploreLabel: '유령체스',
      body: <p className="text-base leading-relaxed">우리가 좋아하는 유령체스!</p>,
    });

    // ⑥ 대화분석
    list.push({
      key: 'chat-stats',
      emoji: '📊',
      title: '대화 분석',
      exploreRoute: '/chat-stats',
      exploreLabel: '대화 분석',
      body: (
        <p className="text-base leading-relaxed">
          6년치 카톡을 분석해 뒀어. 퀴즈 풀고 들어가 봐!
        </p>
      ),
    });

    // ⑦ 시작
    list.push({
      key: 'start',
      emoji: '🙂',
      body: <p className="text-lg leading-relaxed">소개 끝! 이제 천천히 둘러봐.</p>,
    });

    return list;
  }, [boot, welcomeOverride]);

  if (!boot || slides.length === 0) return null;

  const clamped = Math.min(i, slides.length - 1);
  const s = slides[clamped];
  if (!s) return null;
  const isLast = clamped >= slides.length - 1;

  const next = () => (isLast ? onDone() : setI(clamped + 1));
  const prev = () => setI(Math.max(0, clamped - 1));
  const startExplore = () => {
    if (!s.exploreRoute) return;
    navigate(s.exploreRoute);
    setExploring(true);
  };
  const resume = () => {
    setExploring(false);
    next();
  };

  // 둘러보기 모드: 풀스크린 오버레이를 숨기고, 하단에 작은 바만 띄워 실제 메뉴를 만져볼 수 있게.
  if (exploring) {
    return (
      <div
        className="fixed inset-x-0 top-0 z-[60] flex items-center gap-2 border-b border-line bg-card/95 px-4 pb-3 shadow-[0_6px_24px_rgba(0,0,0,.12)] backdrop-blur"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        <p className="flex-1 text-sm text-ink-soft">
          <span className="font-semibold text-ink">{s.exploreLabel}</span> 둘러보는 중
        </p>
        <button onClick={onDone} className="rounded-full px-3 py-2 text-sm text-ink-soft tap-bounce">
          끝내기
        </button>
        <button
          onClick={resume}
          className="rounded-full bg-accentA px-5 py-2 text-sm font-bold text-white tap-bounce"
        >
          {isLast ? '완료' : '다음 ▶'}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-surface px-7 pb-safe pt-safe text-ink animate-[fadeIn_.35s_ease]">
      {/* 은은한 액센트 틸트(읽기 좋게 옅게) */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accentA/15 to-accentB/15" />

      {/* 상단: 건너뛰기 */}
      <div className="relative z-10 flex h-14 items-center justify-end">
        {!isLast && (
          <button
            onClick={onDone}
            className="rounded-full px-3 py-1.5 text-sm font-medium text-ink-soft tap-bounce"
          >
            건너뛰기
          </button>
        )}
      </div>

      {/* 본문 */}
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center text-center">
        <div key={s.key} className="animate-[popIn_.45s_cubic-bezier(.22,1.4,.4,1)]">
          {s.render ? (
            s.render({ next })
          ) : (
            <>
              {s.emoji && <p className="text-6xl">{s.emoji}</p>}
              {s.title && (
                <h2 className="mt-5 font-round text-sm font-bold uppercase tracking-wide text-ink-soft">
                  {s.title}
                </h2>
              )}
              <div className="mt-3 max-w-sm">{s.body}</div>
            </>
          )}
        </div>
      </div>

      {/* 하단: 진행 점 + 이동 */}
      {!s.render && (
        <div className="relative z-10 pb-8">
          <div className="mb-5 flex items-center justify-center gap-2">
            {slides.map((sl, idx) => (
              <span
                key={sl.key}
                className={`h-2 rounded-full transition-all ${
                  idx === clamped ? 'w-6 bg-accentA' : 'w-2 bg-line'
                }`}
              />
            ))}
          </div>
          {s.exploreRoute && (
            <button
              onClick={startExplore}
              className="mb-3 w-full rounded-full border border-line bg-card py-3 text-sm font-semibold text-ink tap-bounce"
            >
              {s.exploreLabel} 직접 둘러보기 →
            </button>
          )}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={prev}
              disabled={clamped === 0}
              className="rounded-full px-5 py-3 text-sm font-semibold text-ink-soft tap-bounce disabled:opacity-0"
            >
              이전
            </button>
            <button
              onClick={next}
              className="flex-1 rounded-full bg-accentA py-3.5 text-base font-bold text-white shadow-lg tap-bounce"
            >
              {isLast ? '시작하기' : '다음'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
